import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Button,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Grid,
  IconButton,
  Chip,
  Skeleton,
  InputAdornment,
  Fab,
  Divider,
  Alert,
  Tooltip,
  Paper,
  Pagination,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  Search,
  Clear,
  LocationOn,
  Email,
  Sms,
  Phone,
  Schedule,
  Home,
} from '@mui/icons-material';
import { getOpenhomes, getPreviousOpenhomes } from '../../service/OFI/getOpentimes';
import { handleMaps } from '../../service/Util/maps';
import { formatTo12Hour, parseDate } from '../../service/Util/dateTimeUtil';
import { getCurrentConfig } from '../../config/config';

const OFI = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openhomes, setOpenhomes] = useState([]);
  const [previousOpenhomes, setPreviousOpenhomes] = useState([]);
  const [filteredOpenhomes, setFilteredOpenhomes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('next14days');
  const [isConnected, setIsConnected] = useState(navigator.onLine);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const config = getCurrentConfig();
   const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => setIsConnected(true);
    const handleOffline = () => setIsConnected(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const getFilterOptions = () => {
    const baseOptions = [
      { label: 'Today', value: 'today' },
      { label: 'Tomorrow', value: 'tomorrow' },
      { label: 'Next 7 Days', value: 'next7days' },
      { label: 'Next 14 Days', value: 'next14days' },
    ];

    if (isConnected) {
      baseOptions.push(
        { label: 'Previous 7 Days', value: 'previous7days' },
        { label: 'Previous 14 Days', value: 'previous14days' }
      );
    }

    return baseOptions;
  };

  const isDateInRange = (dateString, filter) => {
    const itemDate = parseDate(dateString);
    if (!itemDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const next7DaysEnd = new Date(today);
    next7DaysEnd.setDate(today.getDate() + 6);

    const next14DaysEnd = new Date(today);
    next14DaysEnd.setDate(today.getDate() + 13);

    const prev7DaysStart = new Date(today);
    prev7DaysStart.setDate(today.getDate() - 7);

    const prev14DaysStart = new Date(today);
    prev14DaysStart.setDate(today.getDate() - 14);

    itemDate.setHours(0, 0, 0, 0);

    switch (filter) {
      case 'today':
        return itemDate.getTime() === today.getTime();
      case 'tomorrow':
        return itemDate.getTime() === tomorrow.getTime();
      case 'next7days':
        return itemDate >= today && itemDate <= next7DaysEnd;
      case 'next14days':
        return itemDate >= today && itemDate <= next14DaysEnd;
      case 'previous7days':
        return itemDate <= today && itemDate >= prev7DaysStart;
      case 'previous14days':
        return itemDate <= today && itemDate >= prev14DaysStart;
      default:
        return true;
    }
  };

  const applyFilters = (data, searchTerm = searchQuery, dateFilterValue = dateFilter) => {
    let filtered = data;

    filtered = filtered.filter(item => {
      return isDateInRange(item.startDates, dateFilterValue);
    });

    if (searchTerm && searchTerm.length >= 4) {
      const searchTermLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        const isStreetMatch = item.streetName.toLowerCase().includes(searchTermLower);
        const isSuburbMatch = item.suburb.toLowerCase().includes(searchTermLower);
        const isStreetNumberMatch = item.streetNumber.toString().includes(searchTermLower);
        const isFullAddressMatch = `${item.streetNumber} ${item.streetName} ${item.suburb}`.toLowerCase().includes(searchTermLower);
        return isStreetMatch || isSuburbMatch || isStreetNumberMatch || isFullAddressMatch;
      });
    }

    return filtered;
  };

  const getDataSource = () => {
    const isPreviousFilter = dateFilter === 'previous7days' || dateFilter === 'previous14days';
    return isPreviousFilter ? previousOpenhomes : openhomes;
  };

  const fetchOpenhomes = async () => {
    try {
      setIsLoading(true);
      const spanofDays = 14;
      const previousSpanOfDays = 14;
      
      const [openhomesResponse, previousopenhomesRespond] = await Promise.all([
        getOpenhomes(spanofDays),
        getPreviousOpenhomes(previousSpanOfDays),
      ]);

      const fetchedOpenhomes = openhomesResponse.data;
      const fetchedPreviousOpenhomes = previousopenhomesRespond.data;
      
      setOpenhomes(fetchedOpenhomes);
      setPreviousOpenhomes(fetchedPreviousOpenhomes);

      const currentDataSource = (dateFilter === 'previous7days' || dateFilter === 'previous14days')
        ? fetchedPreviousOpenhomes
        : fetchedOpenhomes;
      
      setFilteredOpenhomes(applyFilters(currentDataSource));
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isConnected) {
      fetchOpenhomes();
    }
  }, [isConnected]);

  useEffect(() => {
    const dataSource = getDataSource();
    setFilteredOpenhomes(applyFilters(dataSource));
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchQuery, dateFilter, openhomes, previousOpenhomes]);

  useEffect(() => {
    if (!isConnected && (dateFilter === 'previous7days' || dateFilter === 'previous14days')) {
      setDateFilter('next14days');
    }
  }, [isConnected, dateFilter]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleDateFilterChange = (e) => {
    setDateFilter(e.target.value);
    setCurrentPage(1); // Reset to first page when date filter changes
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredOpenhomes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredOpenhomes.slice(startIndex, endIndex);

  const handleOpenhomeSelect = (item) => {
    navigate('/ofi/ofiapp', { state: { selectedOpenhome: item } });
  };



  const handleCall = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleSMS = (phoneNumber) => {
    window.location.href = `sms:${phoneNumber}`;
  };

  const handleEmail = (email) => {
    window.location.href = `mailto:${email}`;
  };

  const renderPropertyCard = (item) => (
    <Grid  md={4} key={item.propertyopentimeid}>
      <Card 
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6
          }
        }}
        onClick={() => handleOpenhomeSelect(item)}
      >
        <CardMedia
          component="img"
          height="200"
          image={item.propertyPhoto === '' ? config.noListingImage : item.propertyPhoto}
          alt="Property"
          sx={{ objectFit: 'cover' }}
        />
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography variant="h6" component="h2" gutterBottom>
            {`${item.streetNumber} ${item.streetName}`}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <Home sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
            {item.suburb}
          </Typography>
          <Chip
            icon={<Schedule />}
            label={`${item.startDates} ${formatTo12Hour(item.startTimes)} - ${formatTo12Hour(item.endTimes)}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ mt: 1 }}
          />
        </CardContent>
        <Divider />
        <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
          <Tooltip title="View on Map">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleMaps(`${item.streetNumber} ${item.streetName}, ${item.suburb} ${item.State}`, item.latitude, item.longitude);
              }}
              color="primary"
            >
              <LocationOn />
            </IconButton>
          </Tooltip>
          
          {item.sellerEmail && (
            <Tooltip title="Send Email">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleEmail(item.sellerEmail);
                }}
                color="primary"
              >
                <Email />
              </IconButton>
            </Tooltip>
          )}
          
          {item.sellerMobile && (
            <>
              <Tooltip title="Send SMS">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSMS(item.sellerMobile.trim());
                  }}
                  color="primary"
                >
                  <Sms />
                </IconButton>
              </Tooltip>
              <Tooltip title="Call">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCall(item.sellerMobile.trim());
                  }}
                  color="primary"
                >
                  <Phone />
                </IconButton>
              </Tooltip>
            </>
          )}
        </CardActions>
      </Card>
    </Grid>
  );

  const renderSkeleton = () => (
    <Grid container spacing={3}>
      {[...Array(6)].map((_, index) => (
        <Grid  md={4} key={index}>
          <Card>
            <Skeleton variant="rectangular" height={200} />
            <CardContent>
              <Skeleton variant="text" sx={{ fontSize: '1.5rem' }} />
              <Skeleton variant="text" />
              <Skeleton variant="rectangular" width={120} height={24} sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Open For Inspection
        </Typography>
        <Button
          component={Link}
          to="/ofi/add"
          variant="contained"
          startIcon={<Add />}
          size="large"
          sx={{ minWidth: isMobile ? 'auto' : 140 }}
            >
          {isMobile ? 'Add' : 'Add Opentime'}
        </Button>
      </Box>

      {/* Search and Filter Controls */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
           <TextField
              fullWidth
              placeholder="Search openhomes (address, suburb)"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              slots={{
                startAdornment: () => (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchQuery
                  ? () => (
                      <InputAdornment position="end">
                        <IconButton onClick={() => handleSearch('')} edge="end">
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    )
                  : undefined,
              }}
              sx={{
                borderRadius: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Date Filter</InputLabel>
              <Select
                value={dateFilter}
                label="Date Filter"
                onChange={handleDateFilterChange}
              >
                {getFilterOptions().map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Results Count and Pagination Info */}
      {!isLoading && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {filteredOpenhomes.length} open home{filteredOpenhomes.length !== 1 ? 's' : ''} found
            {totalPages > 1 && (
              <span> • Page {currentPage} of {totalPages}</span>
            )}
          </Typography>
          {filteredOpenhomes.length > itemsPerPage && (
            <Typography variant="body2" color="text.secondary">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredOpenhomes.length)} of {filteredOpenhomes.length}
            </Typography>
          )}
        </Box>
      )}

      {/* Property Cards */}
      {isLoading ? (
        renderSkeleton()
      ) : (
        <>
          <Grid container spacing={3}>
            {currentItems.length === 0 ? (
              <Grid item xs={12}>
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                  <Home sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No open homes found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your search or date filter
                  </Typography>
                </Paper>
              </Grid>
            ) : (
              currentItems.map(renderPropertyCard)
            )}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Stack spacing={2}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  shape="rounded"
                  variant="outlined"
                  siblingCount={2}
                  boundaryCount={1}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      backgroundColor: '#f5f5f5',
                      border: '1px solid #e0e0e0',
                      color: '#666',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: '#e3f2fd',
                        borderColor: '#1976d2',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#1976d2',
                        color: 'white',
                        borderColor: '#1976d2',
                        '&:hover': {
                          backgroundColor: '#1565c0',
                        },
                      },
                      '&.MuiPaginationItem-previousNext': {
                        backgroundColor: '#f5f5f5',
                        color: '#666',
                        '&:hover': {
                          backgroundColor: '#e3f2fd',
                        },
                      },
                    },
                  }}
                />
              </Stack>
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default OFI;